import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import SendIcon from "@mui/icons-material/Send";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DescriptionIcon from "@mui/icons-material/Description";
import moment from "moment";
import { TOUCH_QUERY } from "../../../utils/useViewport";

// The five lanes of GET /dashboard/today, re-presented as one filterable feed.
export type FeedType =
  | "reminders"
  | "referrals"
  | "plans"
  | "inactive"
  | "intakes";
export type FeedFilter = "all" | FeedType;

export const FEED_TYPES: FeedType[] = [
  "reminders",
  "plans",
  "referrals",
  "inactive",
  "intakes",
];

// The inactive-clients lane threshold: open cases with no INTERACTION logged
// (server `INTERACTION_ACTIVITY_TYPES` = the case page's Interaction card
// types) in this many days. Mirrors the server default for `?inactiveDays=`
// in GET /dashboard/today — keep the two in step.
export const INACTIVE_DAYS = 180;

// Referrals lane band: in-pipeline referrals (Referred / Appointment
// Scheduled / In Progress) whose START date is between these many days ago.
// Mirrors the server's REFERRAL_LANE_MIN_DAYS / REFERRAL_LANE_MAX_DAYS in
// thrive_server/models/dashboardModel.ts.
export const REFERRAL_MIN_DAYS = 0;

// Upper bound of the referrals lane. Mirrors `REFERRAL_LANE_MAX_DAYS` in
// thrive_server/models/dashboardModel.ts — keep them in step. Referrals past
// it are dropped from the lane and counted in `counts.olderReferrals`,
// surfaced as the "View N older referrals" button.
export const REFERRAL_MAX_DAYS = 90;

interface FeedTypeMeta {
  tileLabel: string;
  chipLabel: string;
  accent: string;
  tint: string;
  iconBg: string;
  icon: typeof AccessAlarmIcon;
  emptyText: string;
}

export const feedTypeMeta: Record<FeedType, FeedTypeMeta> = {
  reminders: {
    tileLabel: "Reminders",
    chipLabel: "Reminders",
    accent: "#9E1F63",
    tint: "#faf0f5",
    iconBg: "#f6e7ef",
    icon: AccessAlarmIcon,
    emptyText: "No reminders pending.",
  },
  referrals: {
    tileLabel: "Referrals",
    chipLabel: "Referrals",
    accent: "#3F51B5",
    tint: "#eef0fb",
    iconBg: "#e8eaf6",
    icon: SendIcon,
    emptyText: "No referrals need attention.",
  },
  plans: {
    tileLabel: "Action Items",
    chipLabel: "Action Items",
    accent: "#0F95B3",
    tint: "#e9f5f8",
    iconBg: "#e2f2f6",
    icon: PlaylistAddCheckIcon,
    emptyText: "No action items due.",
  },
  inactive: {
    tileLabel: "Active / Inactive",
    chipLabel: "Inactive",
    accent: "#c98a00",
    tint: "#fbf5e6",
    iconBg: "#fbf0d6",
    icon: HourglassEmptyIcon,
    emptyText: "No inactive clients.",
  },
  intakes: {
    tileLabel: "Web Intakes",
    chipLabel: "Intakes",
    accent: "#3F5D7D",
    tint: "#eef1f5",
    iconBg: "#e8edf3",
    icon: DescriptionIcon,
    emptyText: "No unassigned intakes.",
  },
};

export const ALL_EMPTY_TEXT = "You're all caught up.";

// Spread into the sx of text set below 12 px: 12 px on touch screens.
export const touchMinTextSx = {
  [`@media ${TOUCH_QUERY}`]: { fontSize: "12px" },
};

export const MAGENTA = "#9E1F63";
export const MAGENTA_DARK = "#851a53";

export const cardSx = {
  backgroundColor: "#ffffff",
  borderRadius: "16px",
  boxShadow: "0 1px 5px rgba(0,0,0,0.08)",
  overflow: "hidden",
};

export const overdueChipSx = {
  backgroundColor: "#fdecea",
  color: "#c62828",
};

export const dueTodayChipSx = {
  backgroundColor: "#fff4d6",
  color: "#8a6d00",
};

export const upcomingChipSx = {
  backgroundColor: "#eef2f6",
  color: "#4a5b6a",
};

// A single unified row in the feed. `raw` is the untouched lane payload.
export interface FeedItem {
  id: string;
  type: FeedType;
  raw: any;
}

// The action-item lane of GET /dashboard/today. Every item carries `nextDue`
// (ISO date of the earliest incomplete task) and the lane arrives sorted by it
// ascending. Future-dated items now come through as "upcoming" — the lane used
// to send overdue/today only, and the old "later" state is gone.
export type ActionItemDueState = "overdue" | "today" | "upcoming";

export interface ActionPlanLaneItem {
  nextDue?: string | null;
  dueState?: ActionItemDueState;
  [key: string]: any;
}

// Days a referral has been in the pipeline, measured from its start date
// (`created_at`, same clock as the server lane). Shared by the FeedRow caption
// and the feed's days-in-pipeline sort so the two can never disagree.
export const referralDaysInPipeline = (r: any): number => {
  const start = r?.created_at || r?.updated_at;
  const parsed = start ? moment(start) : null;
  return parsed && parsed.isValid() ? moment().diff(parsed, "days") : 0;
};
